// Product Info Drawer Component
class ProductInfoDrawer extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #lastTrigger = null;

  /** @type {string} */
  #defaultTitle = 'Product Information';

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLDialogElement} */
  get dialog() {
    const dialog = this.querySelector('dialog');

    if (!(dialog instanceof HTMLDialogElement)) throw new Error('Dialog element not found');

    return dialog;
  }

  /** @type {NodeListOf<HTMLButtonElement>} */
  get triggerButtons() {
    return this.querySelectorAll('.product-info-drawer__trigger');
  }

  /** @type {HTMLButtonElement} */
  get closeButton() {
    const closeButton = this.querySelector('.product-info-drawer__close-button');

    if (!(closeButton instanceof HTMLButtonElement)) throw new Error('Close button not found');

    return closeButton;
  }

  /** @type {HTMLElement | null} */
  get titleElement() {
    return this.querySelector('.product-info-drawer__title');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get panels() {
    return this.querySelectorAll('.product-info-drawer__panel');
  }

  /** @type {HTMLElement | null} */
  get panelsContainer() {
    return this.querySelector('.product-info-drawer__panels');
  }

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (this.titleElement) {
      this.#defaultTitle = this.titleElement.textContent?.trim() || this.#defaultTitle;
    }

    this.#distributeTabBlocks();
    this.#ensureSectionNav();
    this.dispatchEvent(new CustomEvent('pz-display-text:apply', { bubbles: true }));

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.closeButton.addEventListener('click', this.close, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  #distributeTabBlocks() {
    const store = this.querySelector('.product-info-drawer__tab-store');
    const buttons = this.querySelector('.product-info-drawer__buttons');
    const panels = this.querySelector('.product-info-drawer__panels');

    if (!store || !buttons || !panels) return;

    let isFirstPanel = true;

    Array.from(store.children).forEach((child) => {
      // Merchant _divider blocks must not become trailing hairlines when
      // neighboring tabs are hidden (empty Materials / Size, etc.).
      // Visible tabs use CSS adjacent borders instead.
      if (
        child.classList.contains('product-details__divider-block') ||
        child.querySelector?.('.product-details__divider-block, .divider')
      ) {
        child.remove();
        return;
      }

      const trigger = child.querySelector('[data-drawer-part="trigger"]');
      const panel = child.querySelector('[data-drawer-part="panel"]');

      if (trigger instanceof HTMLElement && trigger.querySelector('.product-info-drawer__trigger')) {
        buttons.appendChild(trigger);
      }

      if (panel instanceof HTMLElement) {
        panel.classList.toggle('product-info-drawer__panel--active', isFirstPanel);
        if (isFirstPanel) isFirstPanel = false;
        panels.appendChild(panel);
      }
    });

    this.#syncTriggerDividerState();
  }

  /** Multi-tab rows get dividers between triggers; a single tab does not. */
  #syncTriggerDividerState() {
    const buttons = this.querySelector('.product-info-drawer__buttons');
    if (!buttons) return;

    // Drop orphan dividers / empty trigger shells left after hidden tabs.
    Array.from(buttons.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return;

      if (
        child.classList.contains('product-details__divider-block') ||
        child.classList.contains('product-info-drawer__trigger-divider')
      ) {
        child.remove();
        return;
      }

      if (
        child.matches('[data-drawer-part="trigger"]') &&
        !child.querySelector('.product-info-drawer__trigger')
      ) {
        child.remove();
      }
    });

    const triggerCount = buttons.querySelectorAll('.product-info-drawer__trigger').length;
    buttons.classList.toggle('product-info-drawer__buttons--multi', triggerCount > 1);
    buttons.classList.toggle('product-info-drawer__buttons--single', triggerCount === 1);
    buttons.classList.toggle('product-info-drawer__buttons--empty', triggerCount === 0);
  }

  #ensureSectionNav() {
    const existingNav = this.querySelector('.product-info-drawer__section-nav');
    if (existingNav) existingNav.remove();

    const panels = this.panelsContainer;
    const content = this.querySelector('.product-info-drawer__content');
    if (!panels || !content || this.triggerButtons.length <= 1) return;

    const nav = document.createElement('nav');
    nav.className = 'product-info-drawer__section-nav';
    nav.setAttribute('aria-label', 'Product information sections');

    const dropup = document.createElement('details');
    dropup.className = 'product-info-drawer__section-dropup';

    const trigger = document.createElement('summary');
    trigger.className = 'product-info-drawer__section-dropup-trigger';
    trigger.innerHTML =
      '<span class="product-info-drawer__section-dropup-label">More information</span>' +
      '<span class="product-info-drawer__section-dropup-caret" aria-hidden="true"></span>';

    const panel = document.createElement('div');
    panel.className = 'product-info-drawer__section-dropup-panel';

    const list = document.createElement('ul');
    list.className = 'product-info-drawer__section-nav-list';

    this.triggerButtons.forEach((triggerButton) => {
      const tabName = triggerButton.dataset.tab;
      const label = triggerButton.querySelector('.product-info-drawer__label')?.textContent?.trim();
      if (!tabName || !label) return;

      const item = document.createElement('li');
      item.className = 'product-info-drawer__section-nav-item';

      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'product-info-drawer__section-link';
      link.dataset.tab = tabName;
      link.textContent = label;

      item.appendChild(link);
      list.appendChild(item);
    });

    panel.appendChild(list);
    dropup.appendChild(trigger);
    dropup.appendChild(panel);
    nav.appendChild(dropup);
    panels.after(nav);
  }

  #closeSectionDropup() {
    const dropup = this.querySelector('.product-info-drawer__section-dropup');
    if (dropup instanceof HTMLDetailsElement) {
      dropup.open = false;
    }
  }

  #getPanelTitle(tabName) {
    const trigger = this.querySelector(`.product-info-drawer__trigger[data-tab="${tabName}"]`);
    const triggerLabel = trigger?.querySelector('.product-info-drawer__label')?.textContent?.trim();
    if (triggerLabel) return triggerLabel;

    return this.#defaultTitle;
  }

  #updateDrawerTitle(tabName) {
    const titleElement = this.titleElement;
    if (!titleElement) return;

    titleElement.textContent = this.#getPanelTitle(tabName);
  }

  #updateSectionNav(tabName) {
    this.querySelectorAll('.product-info-drawer__section-link').forEach((link) => {
      if (!(link instanceof HTMLButtonElement)) return;

      const isActive = link.dataset.tab === tabName;
      link.classList.toggle('product-info-drawer__section-link--active', isActive);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  #setSinglePanelMode(active) {
    this.classList.toggle('product-info-drawer--single-panel', active);
    this.toggleAttribute('data-single-panel', active);
  }

  #handleClick = (event) => {
    const trigger = event.target.closest('.product-info-drawer__trigger');
    if (trigger instanceof HTMLButtonElement && this.contains(trigger)) {
      const tabName = trigger.dataset.tab;
      if (!tabName) return;

      this.#lastTrigger = trigger;
      this.open(tabName);
      return;
    }

    const link = event.target.closest('.product-info-drawer__section-link');
    if (link instanceof HTMLButtonElement && this.contains(link)) {
      const tabName = link.dataset.tab;
      if (!tabName) return;

      this.#switchToTab(tabName);
      this.#closeSectionDropup();

      const panels = this.panelsContainer;
      if (panels) {
        panels.scrollTop = 0;
      }
    }
  };

  open = (activeTab) => {
    this.#setSinglePanelMode(true);
    this.dialog.showModal();
    this.setAttribute('open', '');
    this.closeButton.focus();

    const panel = activeTab ? this.querySelector(`[data-panel="${activeTab}"]`) : null;
    if (panel) {
      this.#switchToTab(activeTab);
      return;
    }

    const firstPanel = this.querySelector('.product-info-drawer__panel');
    const firstTabName = firstPanel instanceof HTMLElement ? firstPanel.dataset.panel : null;
    if (firstTabName) {
      this.#switchToTab(firstTabName);
    }
  };

  close = () => {
    this.dialog.close();
    this.removeAttribute('open');
    this.#setSinglePanelMode(false);
    this.#closeSectionDropup();

    const focusTarget = this.#lastTrigger || this.triggerButtons[0];
    if (focusTarget instanceof HTMLButtonElement) {
      focusTarget.focus();
    }
  };

  #switchToTab = (tabName) => {
    this.#setSinglePanelMode(true);

    this.panels.forEach((panel) => panel.classList.remove('product-info-drawer__panel--active'));

    const panel = this.querySelector(`[data-panel="${tabName}"]`);
    if (panel instanceof HTMLElement) {
      panel.classList.add('product-info-drawer__panel--active');
    }

    this.#updateDrawerTitle(tabName);
    this.#updateSectionNav(tabName);
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };
}

if (!customElements.get('product-info-drawer')) {
  customElements.define('product-info-drawer', ProductInfoDrawer);
}
